import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { ROUTES } from "@/constants/routes";

interface BreadcrumbItem {
  label: string;
  path?: string;
  isCurrent?: boolean;
}

interface BreadcrumbConfig {
  [key: string]: BreadcrumbItem[];
}

/**
 * Breadcrumb configuration for routes
 * Define breadcrumbs for specific routes here
 */
const breadcrumbConfig: BreadcrumbConfig = {
  [ROUTES.DASHBOARD]: [{ label: "Dashboard" }],
  [ROUTES.CHILDREN]: [
    { label: "Children", path: ROUTES.CHILDREN },
  ],
  [ROUTES.CHILD_NEW]: [
    { label: "Children", path: ROUTES.CHILDREN },
    { label: "Add Child" },
  ],
  [ROUTES.ASSESSMENTS]: [{ label: "Assessments" }],
  [ROUTES.ASSESSMENTS_NEW]: [
    { label: "Assessments", path: ROUTES.ASSESSMENTS },
    { label: "New Assessment" },
  ],
  [ROUTES.REPORTS]: [{ label: "Reports" }],
  [ROUTES.SETTINGS]: [{ label: "Settings" }],
};

/**
 * Generate breadcrumbs from current pathname
 * Falls back to auto-generated breadcrumbs if not in config
 */
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  // Check exact match first
  if (breadcrumbConfig[pathname]) {
    return breadcrumbConfig[pathname];
  }

  // Handle dynamic routes
  if (pathname.match(/^\/children\/.+$/)) {
    const childId = pathname.split("/")[2];
    if (pathname.includes("/edit")) {
      return [
        { label: "Children", path: ROUTES.CHILDREN },
        { label: "Child Profile", path: `/children/${childId}` },
        { label: "Edit" },
      ];
    }
    return [
      { label: "Children", path: ROUTES.CHILDREN },
      { label: "Child Profile" },
    ];
  }

  if (pathname.match(/^\/assessments\/.+$/)) {
    const assessmentId = pathname.split("/")[2];
    if (pathname.includes("/scoring")) {
      return [
        { label: "Assessments", path: ROUTES.ASSESSMENTS },
        { label: "Assessment", path: `/assessments/${assessmentId}` },
        { label: "Scoring" },
      ];
    }
    if (pathname.includes("/summary")) {
      return [
        { label: "Assessments", path: ROUTES.ASSESSMENTS },
        { label: "Assessment", path: `/assessments/${assessmentId}` },
        { label: "Summary" },
      ];
    }
    return [
      { label: "Assessments", path: ROUTES.ASSESSMENTS },
      { label: "Assessment Details" },
    ];
  }

  // Auto-generate from path segments as fallback
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return [];

  return segments.map((segment, index) => {
    const path = "/" + segments.slice(0, index + 1).join("/");
    const isLast = index === segments.length - 1;
    return {
      label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " "),
      path: isLast ? undefined : path,
      isCurrent: isLast,
    };
  });
}

/**
 * Breadcrumb Navigation Component
 * 
 * Displays a navigation trail showing the user's current location
 * and allowing them to navigate back to parent pages.
 * 
 * @example
 * ```tsx
 * <Breadcrumb />
 * ```
 */
export function Breadcrumb() {
  const location = useLocation();
  const items = generateBreadcrumbs(location.pathname);

  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
        {/* Home link always shown */}
        <li>
          <Link
            to={ROUTES.DASHBOARD}
            className="flex items-center hover:text-foreground transition-colors"
            aria-label="Home"
          >
            <Home className="w-4 h-4" />
          </Link>
        </li>

        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            <ChevronRight className="w-4 h-4 mx-1" aria-hidden="true" />
            {item.path && !item.isCurrent ? (
              <Link
                to={item.path}
                className="hover:text-foreground transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <span
                className="text-foreground font-medium"
                aria-current="page"
              >
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
